import type { Temporal } from '@js-temporal/polyfill'
import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type { PlaybackSegment } from '@lucimark/shared/helpers/playbackScheduling'
import type {
  IPlaybackScheduleRoot,
  IScheduleRootAssignment
} from '@lucimark/shared/models/player-registration-payload'
import type AssetsService from '@/services/AssetsService'
import type { IQueueFrame, TCampaignAsset } from '@/types'
import { resetEmbeddedRandomDeckState } from '@lucimark/shared/helpers/selectEmbeddedFrames'
import { collectScheduleRootAssetUrns } from '@/helpers/collectScheduleRootAssetUrns'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import PlaylistSnapshotService from '@/services/PlaylistSnapshotService'

export interface IPlaybackScheduleLoader {
  queue: IQueueFrame[]
  assets: TCampaignAsset[]
  /** Narrow schedule root when registration includes `scheduleRootUrn`; else null with queue driven by {@link IPlaybackScheduleRoot}. */
  scheduleRoot: IScheduleRootAssignment | null
  set: (root: IPlaybackScheduleRoot | null) => void
  load: (opts?: {
    revalidateCachedEtags?: boolean
    /**
     * Soft-defer rematch: only these URNs may block on network. Other schedule URNs are
     * satisfied from IDB/hybrid locally; missing tail downloads in the background.
     */
    awaitAssetUrns?: readonly string[]
  }) => Promise<void>
  destroy: () => Promise<void>
}

export default class PlaybackScheduleLoader implements IPlaybackScheduleLoader {
  private _assetsService: AssetsService
  private readonly _snapshots = new PlaylistSnapshotService()

  /** Reference last passed to {@link set}. */
  private _lastRoot: IPlaybackScheduleRoot | null = null
  private _allowedAspectRatios: ReadonlySet<AspectRatio> | null = null
  private _excludeFramesWithVideo = false
  private _portalBookViewportAspect: AspectRatio | null = null
  private _operatorMasterPlaylistUrn: string | null = null
  private _geoPoint: import('@lucimark/shared/models/playlist-assignment-geo-filter').TGeoLatLng | null = null
  private _queue: IQueueFrame[] = []
  private _assets: TCampaignAsset[] = []
  /** Bumps when a new soft-defer rest background fetch starts (ignore stale completions). */
  private _restBackgroundFetchGen = 0
  private _onAssetsChange: ((assets: TCampaignAsset[]) => void) | null = null

  constructor(params: {
    assetsService: AssetsService
    onAssetsChange?: (assets: TCampaignAsset[]) => void
  }) {
    this._assetsService = params.assetsService
    this._onAssetsChange = params.onAssetsChange ?? null
  }

  private _configureSnapshots(assignment: IPlaybackScheduleRoot | null = this._lastRoot): void {
    this._snapshots.configure({
      assignment,
      allowedAspectRatios: this._allowedAspectRatios,
      excludeFramesWithVideo: this._excludeFramesWithVideo,
      portalBookViewportAspect: this._portalBookViewportAspect,
      operatorMasterPlaylistUrn: this._operatorMasterPlaylistUrn,
      geoPoint: this._geoPoint
    })
  }

  get queue() {
    return this._queue
  }

  get assets() {
    return this._assets
  }

  get scheduleRoot() {
    return this._snapshots.getEffectiveAssignment()
  }

  /** Effective schedule from registration. */
  getPlaybackScheduleRootEffective(): IPlaybackScheduleRoot | null {
    return this._snapshots.getEffectivePlaybackSchedule()
  }

  private _buildQueue(referenceInstant: Temporal.Instant = getLocalAsUtcInstant()): IQueueFrame[] {
    return this._snapshots.resolveQueueFramesAt(referenceInstant)
  }

  getWorkerPlaybackPlanNow(): PlaybackSegment[] {
    return this._snapshots.resolveWorkerPlaybackPlanAt(getLocalAsUtcInstant())
  }

  peekPlaybackSequenceHashNow(): string {
    return this._snapshots.peekWorkerSequenceHashAt(getLocalAsUtcInstant())
  }

  peekQueueFramesNow(): IQueueFrame[] {
    return this._snapshots.peekQueueFramesAt(getLocalAsUtcInstant())
  }

  getLastQueueDurationSpineAudit() {
    return this._snapshots.getLastQueueDurationSpineAudit()
  }

  getOperatorMasterPlaylistUrn(): string | null {
    return this._operatorMasterPlaylistUrn
  }

  /** Live resolver options for pending-swap head prefetch (must match cutover materialization). */
  getResolveSegmentOptions() {
    return this._snapshots.getResolveSegmentOptions()
  }

  /**
   * {@link PlaylistSnapshotService.scheduleSpineFingerprintForRoot}.
   * Pass the same `referenceInstant` for two roots so time does not advance between compares (boundary flicker).
   */
  scheduleSpineFingerprintForRoot(
    root: IPlaybackScheduleRoot | null,
    referenceInstant: Temporal.Instant = getLocalAsUtcInstant()
  ): string {
    return this._snapshots.scheduleSpineFingerprintForRoot(root, referenceInstant)
  }

  private async _cacheAssets(opts?: {
    revalidateCachedEtags?: boolean
    awaitAssetUrns?: readonly string[]
  }) {
    // Invalidate any in-flight soft-defer rest fetch before replacing `_assets`.
    this._restBackgroundFetchGen++
    const eff = this.getPlaybackScheduleRootEffective()
    const assetURNs = Array.from(collectScheduleRootAssetUrns(eff))
    const meta = eff != null && 'widgetAssetMetadata' in eff ? eff as IScheduleRootAssignment : null
    const awaitSet = opts?.awaitAssetUrns != null && opts.awaitAssetUrns.length > 0
      ? new Set(opts.awaitAssetUrns)
      : null
    if (awaitSet == null) {
      this._assets = await this._assetsService.fetchAssets(
        assetURNs,
        meta?.widgetAssetMetadata,
        meta?.hlsVideoAssetMetadata,
        { revalidateCachedEtags: opts?.revalidateCachedEtags === true }
      )
      return
    }
    const primary = assetURNs.filter(urn => awaitSet.has(urn))
    const rest = assetURNs.filter(urn => !awaitSet.has(urn))
    const primaryAssets = await this._assetsService.fetchAssets(
      primary,
      meta?.widgetAssetMetadata,
      meta?.hlsVideoAssetMetadata,
      { revalidateCachedEtags: opts?.revalidateCachedEtags === true }
    )
    const restLocal = rest.length === 0
      ? []
      : await this._assetsService.fetchAssets(
        rest,
        meta?.widgetAssetMetadata,
        meta?.hlsVideoAssetMetadata,
        { localOnly: true }
      )
    this._assets = [...primaryAssets, ...restLocal]
    if (rest.length > 0) {
      const gen = ++this._restBackgroundFetchGen
      const rootAtStart = this._lastRoot
      void this._assetsService.fetchAssets(
        rest,
        meta?.widgetAssetMetadata,
        meta?.hlsVideoAssetMetadata,
        { revalidateCachedEtags: opts?.revalidateCachedEtags === true }
      ).then((fetched) => {
        if (gen !== this._restBackgroundFetchGen || this._lastRoot !== rootAtStart) {
          return
        }
        const byUrn = new Map(this._assets.map(a => [a.urn, a]))
        for (const a of fetched) {
          byUrn.set(a.urn, a)
        }
        this._assets = [...byUrn.values()]
        this._onAssetsChange?.(this._assets)
      }).catch((err) => {
        console.warn('[PlaybackScheduleLoader] background rest asset fetch failed', err)
      })
    }
  }

  /** Last registration root reference from {@link set}. */
  getLastSetPlaybackRoot(): IPlaybackScheduleRoot | null {
    return this._lastRoot
  }

  set(root: IPlaybackScheduleRoot | null) {
    this._lastRoot = root
    this._configureSnapshots(root)
  }

  /**
   * Resolved player geo point for distribution-list assignment geofilters.
   * Call when GPS updates or when a new schedule root is applied.
   */
  getGeoPointForPlayback(): import('@lucimark/shared/models/playlist-assignment-geo-filter').TGeoLatLng | null {
    return this._geoPoint
  }

  setGeoPointForPlayback(
    point: import('@lucimark/shared/models/playlist-assignment-geo-filter').TGeoLatLng | null
  ): void {
    this._geoPoint = point
    this._configureSnapshots()
  }

  /** Operator-assigned master URN; bypasses that row's `timeBlocks` during segment resolution. */
  setOperatorMasterPlaylistUrn(urn: string | null): void {
    this._operatorMasterPlaylistUrn = urn
    this._configureSnapshots()
  }

  /** Updates viewport aspect filter without changing the assignment (clears cached segment resolution). */
  setAllowedAspectRatiosForPlayback(allowed: ReadonlySet<AspectRatio> | null): void {
    this._allowedAspectRatios = allowed
    this._configureSnapshots()
  }

  /** When true, omit frames with any video facet from the playback queue (low-capacity skip). */
  setExcludeFramesWithVideoForPlayback(exclude: boolean): void {
    this._excludeFramesWithVideo = exclude
    this._configureSnapshots()
  }

  /** Lucimark Book paid orders always match this viewport aspect when registered. */
  setPortalBookViewportAspectForPlayback(aspect: AspectRatio | null): void {
    this._portalBookViewportAspect = aspect
    this._configureSnapshots()
  }

  async reloadAssetsAndQueue(opts?: {
    revalidateCachedEtags?: boolean
    awaitAssetUrns?: readonly string[]
  }): Promise<void> {
    await this._cacheAssets(opts)
    this._queue = this._buildQueue()
  }

  rebuildQueue(): IQueueFrame[] {
    this._queue = this._buildQueue(getLocalAsUtcInstant())
    return this._queue
  }

  rebuildQueueClearingEmbeddedRandomCache(): IQueueFrame[] {
    this._snapshots.clearEmbeddedRandomPickCache()
    resetEmbeddedRandomDeckState()
    this._snapshots.setCommitPortalBookHouseRotatingAdvance(true)
    this._snapshots.setForceNextQueueResolve(true)
    try {
      return this.rebuildQueue()
    }
    finally {
      this._snapshots.setCommitPortalBookHouseRotatingAdvance(false)
    }
  }

  async load(opts?: {
    revalidateCachedEtags?: boolean
    awaitAssetUrns?: readonly string[]
  }) {
    await this._cacheAssets(opts)
    this._queue = this._buildQueue()
  }

  async destroy() {
    this._restBackgroundFetchGen++
    console.warn('destroying playback schedule loader, method not implemented')
  }
}
